// Master Apps — regra única de conformidade documental (8 critérios).
// Extraída da tela de auditoria para ser compartilhada com o acervo de
// documentos: mesma conta, mesmas isenções, em qualquer tela.
//
// Isenções (um critério isento conta como atendido):
//   · apps de visibilidade interna não precisam de regra de negócio,
//     requisitos, repositório nem chamado de origem;
//   · documentação de API só é exigida de api / integracao / fullstack.
import type { App } from '@/lib/catalog-config';

export type ComplianceKey = 'tech' | 'biz' | 'api' | 'req' | 'repo' | 'owner' | 'proj' | 'tick';
export type ComplianceLevel = 'total' | 'parcial' | 'falha';

export interface ComplianceCheck {
  key: ComplianceKey;
  /** Rótulo em PT-BR pronto para a UI. */
  label: string;
  /** Critério atendido, seja por valor cadastrado, seja por isenção. */
  ok: boolean;
  /** A aplicação é isenta deste critério. */
  exempt?: boolean;
  /** Valor cadastrado (link, nome, número); null quando ausente. */
  value?: string | null;
  /** 'doc' = artefato com link; 'meta' = dado de cadastro. */
  kind: 'doc' | 'meta';
}

export interface ComplianceResult {
  checks: ComplianceCheck[];
  /** Critérios atendidos. */
  ok: number;
  /** Total de critérios avaliados (sempre 8). */
  total: number;
  /** ok/total em percentual (0 a 100). */
  pct: number;
  level: ComplianceLevel;
  /** Critérios não atendidos, na ordem da UI. */
  missing: ComplianceCheck[];
}

export const COMPLIANCE_TOTAL = 8;

/** Piso de critérios atendidos para a aplicação ser "parcial" e não "falha". */
export const COMPLIANCE_PARTIAL_FLOOR = 5;

/** Os 5 artefatos documentais do acervo, na ordem em que aparecem na UI. */
export const DOC_KEYS = ['tech', 'biz', 'api', 'req', 'repo'] as const;
export type DocKey = (typeof DOC_KEYS)[number];

export const COMPLIANCE_LABELS: Record<ComplianceKey, string> = {
  tech: 'Documentação técnica',
  biz: 'Regra de negócio',
  api: 'Documentação da API',
  req: 'Levantamento de requisitos',
  repo: 'Repositório de código',
  owner: 'Responsável atribuído',
  proj: 'Número de projeto',
  tick: 'Chamado de origem',
};

/** Ordem canônica dos 8 critérios na UI (a mesma de `complianceOf().checks`). */
export const COMPLIANCE_ORDER: readonly ComplianceKey[] = [
  'tech',
  'biz',
  'api',
  'req',
  'repo',
  'owner',
  'proj',
  'tick',
];

/**
 * Condição de dispensa de cada critério, em PT-BR, para a UI explicar por que
 * um critério pode contar como atendido sem valor cadastrado.
 * `null` = critério sempre exigido.
 */
export const COMPLIANCE_EXEMPTIONS: Record<ComplianceKey, string | null> = {
  tech: null,
  biz: 'Dispensado em aplicações de visibilidade interna',
  api: 'Exigido apenas de aplicações do tipo API, integração ou fullstack',
  req: 'Dispensado em aplicações de visibilidade interna',
  repo: 'Dispensado em aplicações de visibilidade interna',
  owner: null,
  proj: null,
  tick: 'Dispensado em aplicações de visibilidade interna',
};

export const COMPLIANCE_LEVEL_LABELS: Record<ComplianceLevel, string> = {
  total: 'Conformidade total',
  parcial: 'Conformidade parcial',
  falha: 'Não conforme',
};

export const COMPLIANCE_GREEN = '#16a34a';
export const COMPLIANCE_AMBER = '#ca8a04';
export const COMPLIANCE_RED = '#dc2626';

/**
 * Nível de conformidade a partir dos critérios atendidos: `total` exige os 8;
 * `parcial` vale do piso (5) para cima; abaixo disso é `falha`. Extraído em
 * função para a regra ficar legível em três linhas independentes, em vez de uma
 * cadeia de ternários no meio do `return`.
 */
function levelFor(ok: number): ComplianceLevel {
  if (ok === COMPLIANCE_TOTAL) return 'total';
  if (ok >= COMPLIANCE_PARTIAL_FLOOR) return 'parcial';
  return 'falha';
}

/** Documentação de API só é exigida de quem expõe interface programável. */
function apiRequired(app: App): boolean {
  return app.type === 'api' || app.type === 'integracao' || app.type === 'fullstack';
}

/** Chamado de origem: o travessão de cargas antigas conta como ausente. */
function ticketValue(app: App): string | null {
  return app.ticket && app.ticket !== '—' ? app.ticket : null;
}

/** Link cadastrado de um dos 5 artefatos documentais. */
export function docValue(app: App, key: DocKey): string | null {
  const map: Record<DocKey, string | null> = {
    tech: app.docsTechnical,
    biz: app.docsBusiness,
    api: app.docsApi,
    req: app.requirements,
    repo: app.repo,
  };
  return map[key];
}

/** Quantos dos 5 artefatos documentais têm link cadastrado. */
export function docsFilled(app: App): number {
  return DOC_KEYS.filter((k) => !!docValue(app, k)).length;
}

export function complianceOf(app: App): ComplianceResult {
  const internal = app.visibility === 'interno';
  const noApi = !apiRequired(app);
  const ticket = ticketValue(app);

  const checks: ComplianceCheck[] = [
    {
      key: 'tech',
      kind: 'doc',
      label: COMPLIANCE_LABELS.tech,
      value: app.docsTechnical,
      exempt: false,
      ok: !!app.docsTechnical,
    },
    {
      key: 'biz',
      kind: 'doc',
      label: COMPLIANCE_LABELS.biz,
      value: app.docsBusiness,
      exempt: internal,
      ok: !!app.docsBusiness || internal,
    },
    {
      key: 'api',
      kind: 'doc',
      label: COMPLIANCE_LABELS.api,
      value: app.docsApi,
      exempt: noApi,
      ok: !!app.docsApi || noApi,
    },
    {
      key: 'req',
      kind: 'doc',
      label: COMPLIANCE_LABELS.req,
      value: app.requirements,
      exempt: internal,
      ok: !!app.requirements || internal,
    },
    {
      key: 'repo',
      kind: 'doc',
      label: COMPLIANCE_LABELS.repo,
      value: app.repo,
      exempt: internal,
      ok: !!app.repo || internal,
    },
    {
      key: 'owner',
      kind: 'meta',
      label: COMPLIANCE_LABELS.owner,
      value: app.owner || null,
      exempt: false,
      ok: !!app.owner,
    },
    {
      key: 'proj',
      kind: 'meta',
      label: COMPLIANCE_LABELS.proj,
      value: app.project,
      exempt: false,
      ok: !!app.project,
    },
    {
      key: 'tick',
      kind: 'meta',
      label: COMPLIANCE_LABELS.tick,
      value: ticket,
      exempt: internal,
      ok: internal || !!ticket,
    },
  ];

  const ok = checks.filter((c) => c.ok).length;
  const pct = (ok / COMPLIANCE_TOTAL) * 100;

  return {
    checks,
    ok,
    total: COMPLIANCE_TOTAL,
    pct,
    level: levelFor(ok),
    missing: checks.filter((c) => !c.ok),
  };
}

/** Cobertura de UM critério no conjunto de aplicações avaliadas. */
export interface ComplianceCoverage {
  key: ComplianceKey;
  label: string;
  /** Aplicações que atendem ao critério (as dispensadas contam como atendidas). */
  count: number;
  /** Aplicações avaliadas. */
  total: number;
  /** Aplicações dispensadas do critério. */
  exempt: number;
  /** count/total em percentual (0 a 100); 0 quando não há aplicação. */
  pct: number;
  /** Condição de dispensa em PT-BR; null quando o critério é sempre exigido. */
  exemption: string | null;
}

/**
 * Cobertura por critério, na ordem canônica: quantas das `apps` atendem cada um
 * dos 8 critérios. Mesma conta de `complianceOf`, agregada por critério.
 */
export function complianceCoverage(apps: App[]): ComplianceCoverage[] {
  const results = apps.map((a) => complianceOf(a));
  return COMPLIANCE_ORDER.map((key) => {
    let count = 0;
    let exempt = 0;
    for (const r of results) {
      const c = r.checks.find((x) => x.key === key);
      if (!c) continue;
      if (c.ok) count += 1;
      if (c.exempt) exempt += 1;
    }
    return {
      key,
      label: COMPLIANCE_LABELS[key],
      count,
      total: apps.length,
      exempt,
      pct: apps.length > 0 ? (count / apps.length) * 100 : 0,
      exemption: COMPLIANCE_EXEMPTIONS[key],
    };
  });
}

/**
 * Situação de um critério para leitura humana (UI e CSV): o valor cadastrado
 * vence a dispensa, para nunca esconder que o artefato existe.
 */
export function complianceCheckState(
  check: ComplianceCheck,
): 'cadastrado' | 'dispensado' | 'ausente' {
  if (check.value) return 'cadastrado';
  return check.exempt ? 'dispensado' : 'ausente';
}

/** Cor por percentual: verde a partir de 95, âmbar a partir de 75, vermelho abaixo. */
export function complianceColor(pct: number): string {
  if (pct >= 95) return COMPLIANCE_GREEN;
  if (pct >= 75) return COMPLIANCE_AMBER;
  return COMPLIANCE_RED;
}

/** Cor por nível de conformidade (mesma escala da função por percentual). */
export function complianceLevelColor(level: ComplianceLevel): string {
  if (level === 'total') return COMPLIANCE_GREEN;
  if (level === 'parcial') return COMPLIANCE_AMBER;
  return COMPLIANCE_RED;
}

/** Fundo/borda suaves da cor de conformidade, coerentes em claro e escuro. */
export function complianceTone(
  color: string,
  dark: boolean,
): { fg: string; bg: string; border: string } {
  const light: Record<string, string> = {
    [COMPLIANCE_GREEN]: '#dcfce7',
    [COMPLIANCE_AMBER]: '#fef9c3',
    [COMPLIANCE_RED]: '#fee2e2',
  };
  return {
    fg: color,
    bg: dark ? `${color}22` : (light[color] ?? `${color}18`),
    border: dark ? `${color}44` : `${color}33`,
  };
}
